var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
/**
 * @desc sankey chart
 */
import { createRef } from '@byted/light-render';
import * as lodash from '../../utils/lodash';
import { Group, Rect, Path, LinearGradient } from '../../element';
import { createRichText } from '../../rich';
import { SeriesModel } from '../../model';
import { getRectLabelPosition } from '../../utils/textLayout';
import { registerChart } from '../index';
import * as d3Sankey from '../../../third-party/d3-sankey/d3-sankey';
import { createLinePath, sankeyLinkVertical } from './line-link';
import normalizeToArray from '../../utils/normalizeToArray';
var RESET_DELAY = 300;
/**
 * default
 */
var SankeyModel = /** @class */ (function (_super) {
    __extends(SankeyModel, _super);
    function SankeyModel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'sankey';
        _this.resetStyle = function () {
            // the chart may by destroied
            if (!_this.chart) {
                return;
            }
            if (!lodash.isNumber(_this.state.hoverPointIndex) && !_this._hoverLink) {
                _this.state.points.forEach(function (point) {
                    var selected = point.selected;
                    point._rectRef.current.setAttr(__assign(__assign({ opacity: 1, fill: point.color }, _this.getShapeStyle(_this.option.shapeStyle)), (selected ? _this.getShapeStyle(_this.option.selected.shapeStyle) : null)));
                    point._labelRef.current.setAttr(__assign({ opacity: 1 }, _this.getTextStyle(_this.option.label)));
                });
                _this.state.links.forEach(function (link) {
                    return link._pathRef.current.setAttr(__assign({}, _this.getLinkStyle(link)));
                });
            }
        };
        return _this;
    }
    SankeyModel.prototype.getDefaultOption = function () {
        return {
            type: 'sankey',
            name: '',
            nodeWidth: 16,
            nodePadding: 8,
            nodeAlign: 'justify',
            nodeSort: null,
            orient: 'horizontal',
            linkType: null,
            linkShape: 'bezier',
            linkInteractions: ['tooltip', 'hover'],
            constraintLabelInside: true,
            encode: {
                source: 'source',
                target: 'target',
                value: 'value',
            },
            shapeStyle: {
                opacity: 1,
                stroke: null,
                lineWidth: null,
            },
            lineStyle: {
                lineWidth: null,
                stroke: '#6E748E',
                opacity: 0.1,
            },
            label: {
                show: true,
                opacity: 1,
                fontSize: 12,
                position: 'outside',
                distance: 4,
                color: '#6E748E',
                formatter: function (point) { return point.name; },
            },
            hover: {
                label: {
                    show: true,
                    opacity: 1,
                    color: null,
                    position: null,
                    distance: null,
                    fontSize: null,
                },
                shapeStyle: {
                    opacity: 1,
                    stroke: null,
                    lineWidth: null,
                },
                lineStyle: {
                    lineWidth: null,
                    stroke: null,
                    opacity: 0.3,
                },
            },
            selected: {
                shapeStyle: {
                    fill: null,
                    stroke: null,
                    lineWidth: null,
                },
            },
            visible: true,
            showLegend: false,
            selectedMode: false,
        };
    };
    SankeyModel.prototype.getInitialState = function () {
        return {
            links: [],
            points: [],
            needAnimation: false,
            isAllZero: false,
        };
    };
    /**
     * @override
     */
    SankeyModel.prototype.getStateTestData = function () {
        var encode = this.option.encode;
        var sourceNames = this._dataset.uniq(encode.source);
        var targetNames = this._dataset.uniq(encode.target);
        return lodash.uniq(__spreadArrays(sourceNames, targetNames));
    };
    SankeyModel.prototype.beforeRender = function () {
        var _this = this;
        var _a = this.getFacetBBox(), top = _a.top, left = _a.left, right = _a.right, bottom = _a.bottom;
        var _b = this.option, nodeWidth = _b.nodeWidth, nodePadding = _b.nodePadding, nodeSort = _b.nodeSort, orient = _b.orient;
        var colors = this.chart.getOption().colors;
        var topToBottom = orient === 'vertical';
        var nodes = this.getStateTestData().map(function (node, dataIndex) {
            return {
                name: node,
                dataIndex: dataIndex,
                color: colors[dataIndex % colors.length],
                data: node,
                encode: {
                    name: node,
                },
                seriesIndex: _this.seriesIndex,
                visible: true,
                selected: _this.selectedItems.has(node),
                _rectRef: createRef(),
                _labelRef: createRef(),
            };
        });
        if (nodes.length === 0) {
            return this.setState({
                points: [],
                links: [],
            });
        }
        var alignMap = {
            center: d3Sankey.sankeyCenter,
            left: d3Sankey.sankeyLeft,
            right: d3Sankey.sankeyRight,
            justify: d3Sankey.sankeyJustify,
        };
        var layerWidths = normalizeToArray(nodeWidth);
        var avgWidth = lodash.sum(layerWidths) / layerWidths.length;
        var sankey = d3Sankey
            .sankey()
            .nodeAlign(alignMap[this.option.nodeAlign])
            .nodeSort(nodeSort)
            .linkSort(null)
            .nodeId(function (item) { return item.name; })
            .nodeWidth(avgWidth)
            .nodePadding(nodePadding)
            .extent([
            topToBottom ? [top, left] : [left, top],
            topToBottom ? [bottom, right] : [right, bottom],
        ]);
        var layout = sankey({
            nodes: nodes,
            links: this._dataset.mapData(function (record, dataIndex) {
                var encodeData = _this._encode.getEncodeData(record, dataIndex);
                var name = encodeData.source + "-" + encodeData.target;
                return {
                    seriesIndex: _this.seriesIndex,
                    dataIndex: dataIndex,
                    dataType: 'edge',
                    name: name,
                    value: encodeData.value,
                    color: _this.option.lineStyle.stroke,
                    source: encodeData.source,
                    target: encodeData.target,
                    encode: __assign(__assign({}, encodeData), { name: name }),
                    data: record,
                    _pathRef: createRef(),
                };
            }),
        });
        var points = nodes;
        var links = layout.links;
        var maxLayer = lodash.max(points.map(function (point) { return point.layer; }));
        if (lodash.isArray(nodeWidth)) {
            points.forEach(function (point) {
                var _a;
                var layer = point.layer;
                var layerWidth = (_a = nodeWidth[layer]) !== null && _a !== void 0 ? _a : nodeWidth[0];
                var dx = layerWidth - avgWidth;
                if (layer === 0) {
                    point.x1 += dx;
                }
                else if (layer === maxLayer) {
                    point.x0 -= dx;
                }
                else {
                    point.x0 -= dx / 2;
                    point.x1 += dx / 2;
                }
            });
        }
        if (topToBottom) {
            points.forEach(function (point) {
                var x0 = point.x0, x1 = point.x1, y0 = point.y0, y1 = point.y1;
                point.x0 = y0;
                point.x1 = y1;
                point.y0 = x0;
                point.y1 = x1;
            });
        }
        this.setState({
            points: points,
            links: links,
            isAllZero: points.every(function (item) { return item.value === 0; }),
        });
    };
    SankeyModel.prototype.toggleHighlightPoint = function (dataIndex, active) {
        var _this = this;
        var point = this.state.points[dataIndex];
        var sourceLinks = point.sourceLinks, targetLinks = point.targetLinks, selected = point.selected;
        var activeLinks = __spreadArrays(sourceLinks, targetLinks);
        if (lodash.includes(this.option.linkInteractions, 'hover')) {
            activeLinks.forEach(function (link) {
                return link._pathRef.current.setAttr(__assign(__assign({}, _this.getLinkStyle(link)), (active ? _this.getLineStyle(_this.option.hover.lineStyle) : null)));
            });
        }
        point._rectRef.current.setAttr(__assign(__assign(__assign({ fill: point.color }, this.getShapeStyle(this.option.shapeStyle)), (selected ? this.getShapeStyle(this.option.selected.shapeStyle) : null)), (active ? this.getShapeStyle(this.option.hover.shapeStyle) : null)));
    };
    SankeyModel.prototype.getLinkStyle = function (link) {
        var _a = this.option, lineStyle = _a.lineStyle, linkShape = _a.linkShape, linkType = _a.linkType;
        var stroke = lineStyle.stroke;
        var color = stroke;
        if (color === 'source') {
            color = link.source.color;
        }
        if (color === 'target') {
            color = link.target.color;
        }
        if (color === 'gradient') {
            color = new LinearGradient({
                x1: 0,
                y1: 0,
                x2: 1,
                y2: 1,
                stops: [
                    {
                        offset: 0,
                        color: link.source.color,
                    },
                    {
                        offset: 1,
                        color: link.target.color,
                    },
                ],
            });
        }
        var attr = this.getLineStyle(lineStyle);
        if (linkShape === 'bezier' && !linkType) {
            attr.stroke = color;
            attr.fill = 'none';
        }
        else {
            attr.fill = color;
            attr.stroke = 'none';
        }
        return attr;
    };
    // public toggleHighlightPoint(dataIndex: number, active: boolean) {
    //   if (!active) {
    //     return setTimeout(() => this.resetStyle(), RESET_DELAY);
    //   }
    //   const point = this.state.points[dataIndex];
    //   const { sourceLinks, targetLinks, selected } = point;
    //   const activeLinks = [...sourceLinks, ...targetLinks];
    //   const sourceNodes = sourceLinks.map(link => link.target);
    //   const targetNodes = targetLinks.map(link => link.source);
    //   const activeNodes = [...sourceNodes, ...targetNodes, point];
    //   this.grayAll();
    //   activeLinks.forEach(link =>
    //     link._pathRef.current.setAttr({
    //       ...this.getLineStyle(this.option.hover.lineStyle),
    //     }),
    //   );
    //   activeNodes.forEach(node => {
    //     node._labelRef.current.setAttr({
    //       ...this.getTextStyle(this.option.label as LabelOption),
    //     });
    //     node._rectRef.current.setAttr({
    //       ...this.getShapeStyle(this.option.hover.shapeStyle),
    //       ...node.selected ? this.getShapeStyle(this.option.selected.shapeStyle): null,
    //     });
    //   });
    // }
    SankeyModel.prototype.toggleSelectPoint = function (dataIndex, selected) {
        _super.prototype.toggleSelectPoint.call(this, dataIndex, selected);
        var point = this.state.points[dataIndex];
        point._rectRef.current.setAttr(__assign(__assign({ fill: point.color }, this.getShapeStyle(this.option.shapeStyle)), (selected ? this.getShapeStyle(this.option.selected.shapeStyle) : null)));
    };
    /**
     *
     * @override
     */
    SankeyModel.prototype.getSelectedPointIndex = function (item) {
        return lodash.findIndex(this.state.points, function (point) { return point.name === item; });
    };
    SankeyModel.prototype.handleEdgeMouseOver = function (point) {
        if (!lodash.includes(this.option.linkInteractions, 'hover')) {
            return;
        }
        this._hoverLink = point.data;
        this.toggleHighlightLink(point, true);
    };
    SankeyModel.prototype.handleEdgeMouseOut = function (point) {
        if (!lodash.includes(this.option.linkInteractions, 'hover')) {
            return;
        }
        this._hoverLink = null;
        this.toggleHighlightLink(point, false);
    };
    SankeyModel.prototype.toggleHighlightLink = function (link, active) {
        var _this = this;
        if (!active) {
            return setTimeout(function () { return _this.resetStyle(); }, RESET_DELAY);
        }
        this.grayAll();
        link._pathRef.current.setAttr(__assign(__assign({}, this.getLinkStyle(link)), this.getLineStyle(this.option.hover.lineStyle)));
        var linkNodes = [link.source, link.target];
        linkNodes.forEach(function (node) {
            node._labelRef.current.setAttr({
                opacity: 1,
            });
            node._rectRef.current.setAttr({
                opacity: 1,
            });
        });
    };
    SankeyModel.prototype.grayAll = function () {
        var _this = this;
        this.state.links.forEach(function (link) {
            link._pathRef.current.setAttr(__assign({}, _this.getLinkStyle(link)));
        });
        this.state.points.forEach(function (point) {
            point._rectRef.current.setAttr(__assign(__assign({}, _this.getShapeStyle(_this.option.shapeStyle)), (point.selected ? _this.getShapeStyle(_this.option.selected.shapeStyle) : null)));
            // point._label.setAttr({opacity: 0.2});
        });
    };
    SankeyModel.prototype.renderNodes = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('sankey-nodes'),
        });
        this.state.points.forEach(function (point) {
            var x0 = point.x0, y0 = point.y0, x1 = point.x1, y1 = point.y1, color = point.color, selected = point.selected;
            var width = x1 - x0;
            var height = Math.max(y1 - y0, 1);
            var rect = new Rect(__assign(__assign(__assign({ ref: point._rectRef, x: x0, y: y0, width: width,
                height: height, fill: color, r: _this.option.shapeStyle.borderRadius }, _this.getShapeStyle(_this.option.shapeStyle)), (selected ? _this.getShapeStyle(_this.option.selected.shapeStyle) : null)), { onEvent: function (event) {
                    return _this.handleUIEvent(event, 'point', {
                        point: point,
                    });
                } }));
            group.add(rect);
        });
        return group;
    };
    SankeyModel.prototype.renderLinks = function () {
        var _this = this;
        var _a = this.option, linkType = _a.linkType, linkShape = _a.linkShape, orient = _a.orient;
        if (linkType) {
            return this.renderTypeLinks();
        }
        var group = new Group({
            key: this.getKey('sankey-links'),
        });
        var linkFn = orient === 'horizontal' ? d3Sankey.sankeyLinkHorizontal() : sankeyLinkVertical;
        var pathFn = linkShape === 'bezier' ? linkFn : createLinePath;
        this.state.links.forEach(function (link) {
            var path = Path.fromSvgPath(pathFn(link)).setAttr(__assign(__assign({ ref: link._pathRef, lineWidth: Math.max(link.width, 1) }, _this.getLinkStyle(link)), { onMouseEnter: function () { return _this.handleEdgeMouseOver(link); }, onMouseLeave: function () { return _this.handleEdgeMouseOut(link); }, onEvent: function (event) {
                    return _this.handleUIEvent(event, 'edge', {
                        point: link,
                    });
                } }));
            group.add(path);
        });
        return group;
    };
    SankeyModel.prototype.renderTypeLinks = function () {
        var _this = this;
        var linkType = this.option.linkType;
        var group = new Group({
            key: this.getKey('sankey-links'),
        });
        this.state.links.forEach(function (link) {
            var source = link.source, target = link.target;
            var sourceLinks = source.sourceLinks;
            var targetLinks = target.targetLinks;
            var sourceIndex = sourceLinks.indexOf(link);
            var targetIndex = targetLinks.indexOf(link);
            var sourceX = source.x1;
            var targetX = target.x0;
            var cx = (sourceX + targetX) / 2;
            var sourceTop;
            var sourceBottom;
            var targetTop;
            var targetBottom;
            if (linkType === 'overlap') {
                sourceTop = source.y0;
                sourceBottom = source.y1;
                targetTop = target.y0;
                targetBottom = target.y1;
            }
            else if (linkType === 'groupping') {
                sourceTop = source.y0 + (sourceIndex * (source.y1 - source.y0)) / sourceLinks.length;
                sourceBottom =
                    source.y0 + ((sourceIndex + 1) * (source.y1 - source.y0)) / sourceLinks.length;
                targetTop = target.y0 + (targetIndex * (target.y1 - target.y0)) / targetLinks.length;
                targetBottom =
                    target.y0 + ((targetIndex + 1) * (target.y1 - target.y0)) / targetLinks.length;
            }
            var gap = (targetX - sourceX) / 4;
            var path = new Path({
                ref: link._pathRef,
                brush: function (ctx) {
                    ctx.moveTo(sourceX, sourceTop);
                    ctx.bezierCurveTo(cx - gap, sourceTop, cx + gap, targetTop, targetX, targetTop);
                    ctx.lineTo(targetX, targetBottom);
                    ctx.bezierCurveTo(cx + gap, targetBottom, cx - gap, sourceBottom, sourceX, sourceBottom);
                    ctx.closePath();
                },
                fill: _this.option.lineStyle.stroke,
                opacity: _this.option.lineStyle.opacity,
                onEvent: function (event) {
                    return _this.handleUIEvent(event, 'edge', {
                        point: link,
                    });
                },
            });
            group.add(path);
        });
        return group;
    };
    SankeyModel.prototype.renderLabels = function () {
        var _this = this;
        var _a = this.option, orient = _a.orient, constraintLabelInside = _a.constraintLabelInside;
        var group = new Group({
            key: this.getKey('labels'),
        });
        var _b = this.getFacetBBox(), left = _b.left, width = _b.width;
        var labelOption = this.option.label;
        var leftSideDirection = constraintLabelInside ? 'left' : 'right';
        var rightSideDirection = constraintLabelInside ? 'right' : 'left';
        this.state.points.forEach(function (point) {
            var x0 = point.x0, y0 = point.y0, x1 = point.x1, y1 = point.y1;
            var text = _this.formatItem(labelOption.formatter, point);
            var horitontalDirection = x1 - left > width / 2 ? leftSideDirection : rightSideDirection;
            var position = getRectLabelPosition({
                left: x0,
                right: x1,
                top: y0,
                bottom: y1,
            }, labelOption.position, labelOption.distance, orient === 'horizontal' ? horitontalDirection : 'top');
            var label = createRichText(__assign(__assign(__assign({ ref: point._labelRef, display: _this.option.label.show, text: text }, _this.getTextStyle(labelOption)), position), { origin: [position.x, position.y], pointerEvents: 'none' }));
            group.add(label);
        });
        return group;
    };
    SankeyModel.prototype.render = function () {
        var group = new Group({
            key: this.getKey('sankey'),
        });
        if (!this.state.isAllZero) {
            group.add(this.renderLinks());
            group.add(this.renderNodes());
            group.add(this.renderLabels());
        }
        return group;
    };
    return SankeyModel;
}(SeriesModel));
export default SankeyModel;
registerChart('sankey', SankeyModel);
